import { useEffect, useRef, useState, useCallback } from "react";
import { unstable_batchedUpdates } from "react-dom";
import {
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import type { ChangeEvent, DragEvent } from "react";
import {
  useGetPersonDocumentUploadUrl,
  useGetItemDocumentUploadUrl,
  useUploadFileToS3,
  useSanitizeItemDocument,
  useDeleteItemDocument,
} from "../../api/360FilesAPI";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import {
  inspectPdf,
  PDF_CONTENTS_ERROR,
  PDF_PASSWORD_ERROR,
  pdfLocalCheckMessage,
} from "../looksLikePdf";

function normalizeExtension(ext: string): string {
  const trimmed = ext.trim().toLowerCase();
  return trimmed.startsWith(".") ? trimmed : `.${trimmed}`;
}

function getFileExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf(".");
  return dotIndex === -1 ? "" : filename.slice(dotIndex).toLowerCase();
}

function isExtensionAllowed(file: File, acceptedExtensions: string[]): boolean {
  const allowed = acceptedExtensions.map(normalizeExtension);
  return allowed.includes(getFileExtension(file.name));
}

function buildAcceptAttribute(extensions: string[]): string {
  return extensions.map(normalizeExtension).join(",");
}

function fileIdentity(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function mergeSelectedFiles(
  previous: File[],
  incoming: File[],
  multiple: boolean
): File[] {
  if (!multiple) return incoming.slice(0, 1);
  const seen = new Set(previous.map(fileIdentity));
  const appended = incoming.filter((file) => {
    const key = fileIdentity(file);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [...previous, ...appended];
}

interface DragDropUploaderProps {
  personUuid?: string;
  documentType?: "consent-form" | "other" | "resume";
  itemUuid?: string;
  /** When set, only files with these extensions can be selected (e.g. `["pdf", ".docx"]`). */
  acceptedExtensions?: string[];
  multiple?: boolean;
  onUpload?: (
    filenames: string[],
    allSucceeded: boolean
  ) => void | Promise<void>;
}

function DragDropUploader({
  personUuid,
  documentType,
  itemUuid,
  acceptedExtensions,
  multiple = true,
  onUpload,
}: DragDropUploaderProps) {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isCheckingFiles, setIsCheckingFiles] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileCheckGenerationRef = useRef(0);
  const inFlightFileChecksRef = useRef(0);

  const getPersonUploadUrl = useGetPersonDocumentUploadUrl();
  const getItemUploadUrl = useGetItemDocumentUploadUrl();
  const uploadToS3 = useUploadFileToS3();
  const sanitizeItemDocument = useSanitizeItemDocument();
  const deleteItemDocument = useDeleteItemDocument();

  const isReadyToUpload = !!itemUuid || (!!personUuid && !!documentType);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!file) throw new Error("No file selected");

      if (personUuid && !documentType)
        throw new Error("Document type is required");
      if (!personUuid && !itemUuid)
        throw new Error("Either person UUID or item UUID is required");

      if (file.name.toLowerCase().endsWith(".pdf")) {
        const check = await inspectPdf(file);
        if (!check.ok) throw new Error(pdfLocalCheckMessage(check.reason));
      }

      let uploadUrl: string;
      let objectKey: string | undefined;
      if (personUuid && documentType) {
        const { upload_url } = await getPersonUploadUrl.mutateAsync({
          person_uuid: personUuid,
          document_type: documentType,
          filename: file.name,
        });
        uploadUrl = upload_url;
      } else if (itemUuid) {
        const { upload_url, object_key } = await getItemUploadUrl.mutateAsync({
          item_uuid: itemUuid,
          filename: file.name,
        });
        uploadUrl = upload_url;
        objectKey = object_key;
      } else {
        throw new Error("Either person UUID or item UUID is required");
      }

      await uploadToS3.mutateAsync({ uploadUrl, file });
      if (itemUuid && objectKey && file.name.toLowerCase().endsWith(".pdf")) {
        try {
          await sanitizeItemDocument.mutateAsync({
            item_uuid: itemUuid,
            object_key: objectKey,
          });
        } catch (sanitizeErr) {
          try {
            await deleteItemDocument.mutateAsync({
              itemUuid,
              filename: file.name,
            });
          } catch (deleteErr) {
            console.error("Failed to remove rejected upload:", deleteErr);
          }
          throw sanitizeErr;
        }
      }
    },
    [
      personUuid,
      documentType,
      itemUuid,
      getPersonUploadUrl,
      getItemUploadUrl,
      uploadToS3,
      sanitizeItemDocument,
      deleteItemDocument,
    ]
  );

  const beginFileCheck = () => {
    inFlightFileChecksRef.current += 1;
    setIsCheckingFiles(true);
    return fileCheckGenerationRef.current;
  };

  const endFileCheck = (generation: number) => {
    if (generation !== fileCheckGenerationRef.current) return;
    inFlightFileChecksRef.current = Math.max(
      0,
      inFlightFileChecksRef.current - 1
    );
    if (inFlightFileChecksRef.current === 0) {
      setIsCheckingFiles(false);
    }
  };

  const cancelPendingFileChecks = () => {
    fileCheckGenerationRef.current += 1;
    inFlightFileChecksRef.current = 0;
    setIsCheckingFiles(false);
  };

  useEffect(() => {
    return () => {
      fileCheckGenerationRef.current += 1;
      inFlightFileChecksRef.current = 0;
    };
  }, []);

  async function handleSelectFiles(files: FileList) {
    if (!files.length) return;

    const generation = beginFileCheck();
    const incoming = multiple ? Array.from(files) : [files[0]];
    let valid = incoming;
    let extensionError: string | null = null;
    const accepted: File[] = [];
    let hasEncryptedPdf = false;
    let hasInvalidPdf = false;

    try {
      if (acceptedExtensions?.length) {
        valid = incoming.filter((file) =>
          isExtensionAllowed(file, acceptedExtensions)
        );
        if (valid.length < incoming.length) {
          const label = acceptedExtensions.map(normalizeExtension).join(", ");
          extensionError = `Only ${label} files are allowed`;
        }
      }

      for (const file of valid) {
        if (generation !== fileCheckGenerationRef.current) return;
        if (file.name.toLowerCase().endsWith(".pdf")) {
          const check = await inspectPdf(file);
          if (generation !== fileCheckGenerationRef.current) return;
          if (!check.ok) {
            if (check.reason === "encrypted") hasEncryptedPdf = true;
            else hasInvalidPdf = true;
            continue;
          }
        }
        accepted.push(file);
      }
    } finally {
      // React 17 does not batch setState after await; keep file results and
      // the checking flag in the same commit so Accept cannot run against a
      // replaced or still-empty selection.
      unstable_batchedUpdates(() => {
        if (generation === fileCheckGenerationRef.current) {
          const nextError = hasEncryptedPdf
            ? PDF_PASSWORD_ERROR
            : hasInvalidPdf
            ? PDF_CONTENTS_ERROR
            : extensionError;
          setError(nextError);
          if (accepted.length) {
            setSuccess(null);
            setSelectedFiles((prev) =>
              mergeSelectedFiles(prev, accepted, multiple)
            );
          }
        }
        endFileCheck(generation);
      });
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (isUploading) return;
    void handleSelectFiles(event.dataTransfer.files);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.files?.length || isUploading) return;
    void handleSelectFiles(event.target.files);
    event.target.value = "";
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
  }

  function handleClick() {
    if (isUploading || isCheckingFiles) return;
    inputRef.current?.click();
  }

  function handleBack() {
    cancelPendingFileChecks();
    setSelectedFiles([]);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  async function handleAccept() {
    if (!selectedFiles.length || inFlightFileChecksRef.current > 0) return;

    if (!isReadyToUpload) {
      setError("Upload is not ready. Missing person or item information.");
      return;
    }

    setError(null);
    setSuccess(null);
    setIsUploading(true);

    const uploadedFilenames: string[] = [];
    const failedFiles: File[] = [];

    for (const file of selectedFiles) {
      try {
        await uploadFile(file);
        uploadedFilenames.push(file.name);
      } catch {
        failedFiles.push(file);
      }
    }

    const allSucceeded = failedFiles.length === 0;
    const failedFilenames = failedFiles.map((file) => file.name);

    let refreshSucceeded = true;
    if (uploadedFilenames.length > 0 && onUpload) {
      try {
        await onUpload(uploadedFilenames, allSucceeded);
      } catch {
        refreshSucceeded = false;
      }
    }

    if (allSucceeded && refreshSucceeded) {
      setSuccess(
        uploadedFilenames.length === 1
          ? "File uploaded successfully"
          : "Files uploaded successfully"
      );
      setSelectedFiles([]);
    } else if (uploadedFilenames.length > 0) {
      if (allSucceeded && !refreshSucceeded) {
        setError(
          "Files uploaded but failed to refresh the document list. Please try again."
        );
        setSelectedFiles([]);
      } else {
        const uploadError =
          failedFilenames.length === 1
            ? `Uploaded ${uploadedFilenames.length} file(s). Failed to upload: ${failedFilenames[0]}`
            : `Uploaded ${uploadedFilenames.length} of ${
                selectedFiles.length
              } files. Failed: ${failedFilenames.join(", ")}`;
        setError(
          refreshSucceeded
            ? uploadError
            : `${uploadError}. Failed to refresh the document list.`
        );
        setSelectedFiles(failedFiles);
      }
    } else {
      setError(
        failedFilenames.length === 1
          ? "Failed to upload file"
          : "Failed to upload files"
      );
    }

    setIsUploading(false);
  }

  const showConfirmation = selectedFiles.length > 0;
  const isBusy = isUploading || isCheckingFiles;

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        aria-label="Upload file"
        disabled={isUploading}
        multiple={multiple}
        accept={
          acceptedExtensions?.length
            ? buildAcceptAttribute(acceptedExtensions)
            : undefined
        }
      />

      {!showConfirmation ? (
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
            isBusy ? "opacity-50 pointer-events-none" : "hover:border-primary"
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={handleClick}
          aria-disabled={isBusy}
          tabIndex={0}
          role="button"
        >
          {isCheckingFiles ? (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <ArrowPathIcon className="animate-spin w-4 h-4" />
              Checking files...
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              Drag & drop or <span className="underline">browse</span> to upload
            </span>
          )}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-900 mb-3">
            Confirm upload
          </p>
          <ul className="space-y-2 mb-4">
            {selectedFiles.map((file, idx) => (
              <li
                key={`${file.name}-${idx}`}
                className="flex items-center gap-2 text-sm text-gray-700 min-w-0"
              >
                <PaperClipIcon className="w-4 h-4 flex-shrink-0 text-gray-500" />
                <span className="truncate" title={file.name}>
                  {file.name}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
            <Button
              text="Back"
              type={ButtonTypes.back}
              colorType={ButtonColorTypes.cancel}
              onClick={handleBack}
              disabled={isUploading}
              className="rounded-lg border-gray-300"
            />
            <Button
              text={
                isUploading
                  ? "Uploading..."
                  : isCheckingFiles
                  ? "Checking files..."
                  : "Accept"
              }
              colorType={ButtonColorTypes.primary}
              onClick={handleAccept}
              disabled={isBusy}
              className="rounded-lg"
            />
          </div>
          {isBusy && (
            <div className="flex items-center justify-center gap-2 mt-3 text-sm text-gray-500">
              <ArrowPathIcon className="animate-spin w-4 h-4" />
              {isUploading ? "Uploading..." : "Checking files..."}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-destructive text-xs mt-2">
          <ExclamationCircleIcon className="w-5 h-5" /> {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 text-success text-xs mt-2">
          <CheckCircleIcon className="w-5 h-5" /> {success}
        </div>
      )}
    </div>
  );
}

export default DragDropUploader;
